// 첫 화면의 **창 안 설정 화면**(2026-09-29 사용자 결정) — ≡ 메뉴 [설정] 을 누르면 카드 자리를 이 화면이 대신한다.
//
// 왜: 설정을 서랍에 모두 넣으니 작은 창에서 위아래가 잘려 스크롤이 많았고(사용자: "보기 안 좋다"), 별도 창은 원치 않았다.
// ⇒ 메인 창 안에서 보이되, 열려 있는 동안 **창 높이를 내용에 맞춰 늘린다**(무인 접속 칸을 펼치면 더 늘어난다).
//    닫으면([메인 화면으로]) 첫 화면이 원래 배치를 다시 적용한다(`LoginView` 의 배치 효과).
import { useEffect, useRef } from 'react';
import { fitMainWindowHeight } from '../../appShell/mainWindow';
import { MyConnectionSettings } from './MyConnectionSettings';
import type { MyConnection } from './useMyConnection';

/**
 * @param conn   첫 화면과 같은 상태(구독은 첫 화면이 한 번만)
 * @param onBack [메인 화면으로] · Esc · 머리줄 제목 누름
 */
export function SettingsPage({ conn, onBack }: { conn: MyConnection; onBack: () => void }) {
  const pageRef = useRef<HTMLDivElement>(null);

  // 창 높이 = 지금 창 높이 + **스크롤 영역이 실제로 넘친 양**(부모 `.home-scroll` 의 scrollHeight − clientHeight).
  // 🔴 종전엔 "머리줄 높이 + 이 화면 높이" 로 더했는데 그 사이 여백이 빠져 수십 px 모자랐다 → 항목이 하나 더 있는
  //    Mac(자동 실행)에서 스크롤이 생겼다(2026-09-29 사용자 Mac 실기). 넘친 양을 직접 재면 여백 구성과 무관하게 맞다.
  //    넘치지 않으면 창을 그대로 둔다(줄이지 않는다 — 열고 닫을 때 창이 출렁이지 않게. 닫으면 첫 화면이 원래 배치로 되돌린다).
  useEffect(() => {
    const el = pageRef.current;
    const scroller = el?.parentElement;
    if (!el || !scroller) return;
    const fit = () => {
      const overflow = scroller.scrollHeight - scroller.clientHeight;
      if (overflow > 0) fitMainWindowHeight(window.innerHeight + overflow);
    };
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    void document.fonts?.ready.then(fit); // 글꼴이 늦게 오면 높이가 바뀐다
    return () => ro.disconnect();
  }, []);

  // Esc = 뒤로(확인 창·입력칸이 먼저 처리한 Esc 는 건드리지 않는다).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack]);

  return (
    <div ref={pageRef} className="settings-page">
      {/* 돌아가는 길은 크고 분명하게(2026-09-29 사용자 요청 — "뒤로" 대신 "메인 화면으로"). 머리줄 제목을 눌러도 돌아간다(LoginView). */}
      <button className="settings-home-btn" onClick={onBack}>
        ← 메인 화면으로
      </button>
      <h2 className="home-h settings-page-title">내 기기 설정</h2>
      <MyConnectionSettings conn={conn} />
    </div>
  );
}
